import { formatFileSize } from '../../utils/formatters';
import type { EDiscoveryExport, EDiscoverySource } from '../../types';

/** Purview statuses whose export has files to import. */
const IMPORTABLE_STATUSES = new Set(['succeeded', 'partiallySucceeded']);

export function isImportable(exp: Pick<EDiscoveryExport, 'status' | 'expired'>): boolean {
  return !exp.expired && IMPORTABLE_STATUSES.has(exp.status ?? 'succeeded');
}

export const EXPORT_STATUS_LABELS: Record<string, string> = {
  notStarted: 'Queued',
  running: 'Running',
  failed: 'Failed',
  partiallySucceeded: 'Partial',
};

/** The tag on an export that can't be ticked ("Expired", "Running", …), or null. */
export function exportBadge(exp: Pick<EDiscoveryExport, 'status' | 'expired'>): string | null {
  if (exp.expired) return 'Expired';
  if (isImportable(exp) || !exp.status) return null;
  return EXPORT_STATUS_LABELS[exp.status] ?? exp.status;
}

/** "date · author · N packages · size" — packages/size only once the export has finished. */
export function exportMeta(exp: EDiscoveryExport): string {
  const parts: string[] = [];
  if (exp.created_at) parts.push(new Date(exp.created_at).toLocaleString());
  if (exp.created_by) parts.push(exp.created_by);
  if (isImportable(exp)) {
    parts.push(`${exp.package_count} ${exp.package_count === 1 ? 'package' : 'packages'}`);
    if (exp.total_size) parts.push(formatFileSize(exp.total_size));
  }
  return parts.join(' · ');
}

/** The single status line under an expanded source, for every state. */
export function sourceStatusLine(source: Pick<EDiscoverySource, 'status' | 'error' | 'files'>): string {
  const total = source.files.length;
  const done = source.files.filter((f) => f.status === 'ingested').length;
  switch (source.status) {
    case 'pending':
    case 'preparing':
      return 'Preparing export…';
    case 'ready':
      return `Importing ${total} ${total === 1 ? 'file' : 'files'}…`;
    case 'ingesting':
      return total ? `Importing files… ${done}/${total}` : 'Importing files…';
    case 'ingested':
      return `Imported ${done} of ${total} ${total === 1 ? 'file' : 'files'}`;
    case 'failed':
      return source.error || 'Failed to prepare';
  }
}

/** One line under the export checklist saying what "Open case" will import. */
export function selectionHint(count: number, systemLabel: string): string {
  if (count === 0) return `Tick the exports to import from ${systemLabel}.`;
  return `${count} ${count === 1 ? 'export' : 'exports'} will be imported when you open the case.`;
}

export const EDISCOVERY_SYSTEM_LABELS: Record<string, string> = {
  purview: 'Purview',
  jatheon: 'Jatheon',
  mock: 'Mock (demo)',
};

/** One eDiscovery import running for a case, as the documents panel shows it. */
export interface ImportProgress {
  id: string;
  /** "Importing exports from Purview…" */
  title: string;
  /** "Preparing export…", "3 of 5 files imported", or the error. */
  detail: string;
  /** 0-100, or null while the size isn't known yet (indeterminate bar). */
  percent: number | null;
  failed: boolean;
}

const RUNNING = new Set(['pending', 'preparing', 'ready', 'ingesting']);

/**
 * The imports to show on the case page (FB-1007 #4): every source still being
 * prepared or ingested, and failed ones so the failure isn't silent. Finished
 * sources drop out; their documents are in the list by then.
 */
export function importProgress(sources: EDiscoverySource[]): ImportProgress[] {
  return sources
    .filter((s) => RUNNING.has(s.status) || s.status === 'failed')
    .map((s) => {
      const system = EDISCOVERY_SYSTEM_LABELS[s.system] ?? s.system;
      const total = s.files.length;
      const done = s.files.filter((f) => f.status === 'ingested' || f.status === 'failed').length;
      if (s.status === 'failed') {
        return {
          id: s.id,
          title: `Import from ${system} failed`,
          detail: s.error || 'The export could not be imported.',
          percent: null,
          failed: true,
        };
      }
      const preparing = s.status === 'pending' || s.status === 'preparing';
      return {
        id: s.id,
        title: `Importing exports from ${system}…`,
        detail: preparing
          ? `Preparing ${s.case_name ? `“${s.case_name}”` : 'the export'}…`
          : `${done} of ${total} ${total === 1 ? 'file' : 'files'} imported`,
        percent: preparing || total === 0 ? null : Math.round((done / total) * 100),
        failed: false,
      };
    });
}
